import { convertToLocalDateTime } from "components/helpers";
import { Icon, Loader } from "semantic-ui-react";

import { Checkbox } from "@/components/ui/checkbox";
import TagList from "components/TagList";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import CallModal from "components/modals/CallModal";
import DepartmentService from "services/Department";
import EntityService from "services/Entity";
import CampaignService from "services/Campaign";
import { ContactTableActions } from "./ContactTableActions";
import { getUsersForFiltersQuery } from "helpers/user";
import { NULLABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

export const getContactsTableColumns = ({
  handleDeleteContact,
  invalidateData,
  enableCalling,
  isVoicemailsFetched,
  voicemails,
}) => {
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("ID")}
        </TableLink>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType="Contact"
          modelId={row.getValue("ID")}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "full_name",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Alternate Names",
      id: "Alternate Names",
      accessorKey: "alternate_names",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Alternate Names").map(({ id, name }) => (
            <li key={id}>{name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
    },
    {
      header: "Emailable",
      id: "Emailable",
      accessorKey: "emailable",
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          <Icon
            name={row.getValue("Emailable") ? "check" : "close"}
            color={row.getValue("Emailable") ? "green" : "red"}
          />
        </div>
      ),
    },
    {
      header: "Callable",
      id: "Callable",
      accessorKey: "callable",
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          <Icon
            name={row.getValue("Callable") ? "check" : "close"}
            color={row.getValue("Callable") ? "green" : "red"}
          />
        </div>
      ),
    },
    {
      header: "Department",
      id: "Department",
      accessorKey: "department_name",
    },
    {
      header: "Alternate Emails",
      id: "Alternate Emails",
      accessorKey: "email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row
            .getValue("Alternate Emails")
            .filter(email => !email.is_primary)
            .map(({ id, email }) => (
              <li key={id}>{email}</li>
            ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Phone Numbers",
      id: "Phone Numbers",
      accessorKey: "phone_numbers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Phone Numbers").map(phone => (
            <li key={phone.id}>
              {phone.ext ? `${phone.number}x${phone.ext}` : phone.number}
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Timezone",
      id: "Timezone",
      accessorKey: "timezone",
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity_name",
      cell: ({ row }) => (
        <TableLink href={`/entities/${row.original.entity_id}`}>
          {row.getValue("Entity")}
        </TableLink>
      ),
    },
    {
      header: "Title",
      id: "Title",
      accessorKey: "title",
    },
    {
      header: "Campaigns",
      id: "Campaigns",
      accessorKey: "campaign_targets",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.getValue("Campaigns").map(({ campaign_id, campaign_name }) => (
            <li key={`${row.original.id}-${campaign_id}`}>
              <TableLink href={`/campaigns/${campaign_id}`}>
                {campaign_name}
              </TableLink>
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Account Managers",
      id: "Account Managers",
      accessorKey: "account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row
            .getValue("Account Managers")
            .map(({ user_id, user_full_name }) => (
              <li key={`${row.original.id}-${user_id}`}>{user_full_name}</li>
            ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Note Count",
      id: "Note Count",
      accessorKey: "notes_count",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.getValue("ID")}/notes`}>
          {row.getValue("Note Count") ? row.getValue("Note Count") : 0}
        </TableLink>
      ),
    },
    {
      header: "Last Activity Date",
      id: "Last Activity Date",
      accessorKey: "last_activity_date",
      cell: ({ row }) => (
        <div>
          {row.getValue("Last Activity Date")
            ? convertToLocalDateTime(row.getValue("Last Activity Date")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    ...(enableCalling
      ? [
          {
            header: "Call",
            id: "Call",
            enableSorting: false,
            cell: ({ row }) => {
              const contact = row.original;
              return isVoicemailsFetched ? (
                <CallModal
                  contactId={contact.id}
                  disabled={
                    !contact.phone_numbers.length ||
                    !contact.phone_numbers.some(
                      ({ is_callable }) => is_callable
                    ) ||
                    !contact.phone_numbers.some(({ enabled }) => enabled)
                  }
                  voicemails={voicemails}
                />
              ) : (
                <Loader size="mini" active />
              );
            },
          },
        ]
      : []),
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <ContactTableActions
          contact={row.original}
          handleDeleteContact={handleDeleteContact}
        />
      ),
    },
  ];
};

export const getContactsTableFilters = ({ tags, entityId }) => {
  return [
    {
      key: "last_activity_date",
      title: "Last Activity",
      type: "dateRange",
    },
    {
      key: "search:contact_name",
      title: "Contact Name",
      type: "input",
    },
    {
      key: "search:contact_email",
      title: "Contact Email",
      type: "input",
    },
    {
      key: "search:contact_phone",
      title: "Contact Phone",
      type: "input",
    },
    {
      key: "department_id",
      title: "Department Name",
      type: "ruvixxSelect",
      queryFn: DepartmentService.getForFilters,
    },
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "assigned_user_id",
      title: "Assignee",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    ...(!entityId
      ? [
          {
            key: "entity_id",
            title: "Entity",
            type: "ruvixxSelect",
            queryFn: EntityService.getEntitiesForFilters,
          },
        ]
      : []),
    {
      key: "campaign_id",
      title: "Campaign",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: CampaignService.getCampaignsForFilters,
      props: {
        includeNoneOption: true,
      },
    },
    {
      title: "Outreach Permissions",
      key: "grid_1",
      type: "grid",
      components: [
        { key: "is_callable", title: "Callable" },
        { key: "not_callable", title: "Not Callable" },
        { key: "is_emailable", title: "Emailable" },
        { key: "not_emailable", title: "Not Emailable" },
      ],
    },
  ];
};
